import type { AppMode, AtlasBasemap, AtlasLens, Confidence, DisplayProfile, FilterState, NodeType, RelationType, UrlState } from '../data/types';

const nodeTypes: NodeType[] = ['company', 'person', 'architecture', 'place', 'machine', 'technology', 'process', 'textile', 'document', 'photograph', 'map', 'event', 'trade', 'heritage'];
const relationTypes: RelationType[] = ['located-at', 'owned-by', 'operated', 'powered', 'produced', 'used-in', 'transformed', 'documented-by', 'depended-on', 'exported-to', 'succeeded-by', 'damaged-by', 'preserved-by', 'part-of', 'influenced', 'connects'];
const confidences: Confidence[] = ['high', 'medium', 'low'];

const defaultFilters = (): FilterState => ({
  search: '',
  nodeTypes: [],
  relationTypes: [],
  confidence: [],
  sourceIds: [],
  mediaOnly: false,
});

const values = <T extends string>(value: string | null, allowed: T[]): T[] => {
  if (!value) return [];
  const allowedSet = new Set(allowed);
  return value.split(',').filter((item): item is T => allowedSet.has(item as T));
};

const list = (value: string | null): string[] => value ? value.split(',').filter(Boolean).slice(0, 80) : [];

export function readUrlState(href = window.location.href): UrlState {
  const params = new URL(href).searchParams;
  const requestedMode = params.get('mode');
  const mode: AppMode = requestedMode === 'presentation' ? 'presentation' : requestedMode === 'atlas' ? 'atlas' : 'research';
  const profile = params.get('profile') === 'presenter' ? 'presenter' : 'exhibition';
  const step = Number(params.get('step'));
  const atlasYear = Number(params.get('year'));
  const atlasStep = Number(params.get('atlasStep'));
  const requestedLens = params.get('lens');
  const atlasLens: AtlasLens = requestedLens === 'time' || requestedLens === 'flight' ? requestedLens : 'places';
  const atlasBasemap: AtlasBasemap = params.get('basemap') === 'ortho' ? 'ortho' : 'cartography';
  return {
    mode,
    profile,
    nodeId: params.get('node') || undefined,
    compareId: params.get('compare') || undefined,
    storyId: params.get('story') || undefined,
    stepIndex: Number.isFinite(step) && step >= 0 ? Math.floor(step) : 0,
    filters: {
      search: params.get('q') || '',
      nodeTypes: values(params.get('types'), nodeTypes),
      relationTypes: values(params.get('relations'), relationTypes),
      confidence: values(params.get('confidence'), confidences),
      sourceIds: list(params.get('sources')),
      mediaOnly: params.get('media') === '1',
      from: params.get('from') || undefined,
      to: params.get('to') || undefined,
    },
    pinnedIds: list(params.get('pins')),
    historyIds: list(params.get('history')),
    pathRelationIds: list(params.get('path')),
    atlasLens,
    atlasBasemap,
    atlasYear: Number.isFinite(atlasYear) ? Math.min(2026, Math.max(1760, Math.floor(atlasYear))) : 2026,
    atlasSiteId: params.get('site') || undefined,
    atlasTourId: params.get('tour') || undefined,
    atlasStepIndex: Number.isFinite(atlasStep) && atlasStep >= 0 ? Math.floor(atlasStep) : 0,
  };
}

function setList(params: URLSearchParams, key: string, valuesToSet: string[]): void {
  if (valuesToSet.length > 0) params.set(key, valuesToSet.join(','));
  else params.delete(key);
}

export function writeUrlState(state: UrlState, replace = true): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  params.set('mode', state.mode);
  if (state.profile !== 'exhibition') params.set('profile', state.profile);
  if (state.nodeId) params.set('node', state.nodeId);
  if (state.compareId) params.set('compare', state.compareId);
  if (state.storyId) params.set('story', state.storyId);
  if (state.stepIndex > 0) params.set('step', String(state.stepIndex));
  if (state.filters.search) params.set('q', state.filters.search);
  setList(params, 'types', state.filters.nodeTypes);
  setList(params, 'relations', state.filters.relationTypes);
  setList(params, 'confidence', state.filters.confidence);
  setList(params, 'sources', state.filters.sourceIds);
  if (state.filters.mediaOnly) params.set('media', '1');
  if (state.filters.from) params.set('from', state.filters.from);
  if (state.filters.to) params.set('to', state.filters.to);
  setList(params, 'pins', state.pinnedIds);
  setList(params, 'history', state.historyIds);
  setList(params, 'path', state.pathRelationIds);
  if (state.atlasLens !== 'places') params.set('lens', state.atlasLens);
  if (state.atlasBasemap !== 'cartography') params.set('basemap', state.atlasBasemap);
  if (state.atlasYear !== 2026) params.set('year', String(state.atlasYear));
  if (state.atlasSiteId) params.set('site', state.atlasSiteId);
  if (state.atlasTourId) params.set('tour', state.atlasTourId);
  if (state.atlasStepIndex > 0) params.set('atlasStep', String(state.atlasStepIndex));
  const next = `${window.location.pathname}?${params.toString()}`;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', next);
}

export { defaultFilters };
